import { useAuth } from '@/contexts/AuthContext';
import { useLocale } from '@/i18n/LocaleContext';
import type { Locale } from '@/i18n/types';
import { isStandaloneDisplay } from '@/lib/pwa';
import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import './LandingPage.css';

export function LandingPage() {
  const { loginWithCognito, authError, clearAuthError } = useAuth();
  const { locale, setLocale, t, format } = useLocale();
  const revealRefs = useRef<HTMLElement[]>([]);
  const [isStandalone, setIsStandalone] = useState(isStandaloneDisplay);

  useEffect(() => {
    const sync = () => setIsStandalone(isStandaloneDisplay());
    sync();

    const mq = window.matchMedia('(display-mode: standalone)');
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    if (authError) {
      const timer = setTimeout(() => clearAuthError(), 5000);
      return () => clearTimeout(timer);
    }
  }, [authError, clearAuthError]);

  useEffect(() => {
    document.documentElement.classList.add('landing-active');
    document.body.classList.add('landing-active');
    return () => {
      document.documentElement.classList.remove('landing-active');
      document.body.classList.remove('landing-active');
    };
  }, []);

  useEffect(() => {
    if (isStandalone) return;

    const nodes = revealRefs.current.filter(Boolean);
    if (nodes.length === 0) return;

    const show = (el: Element) => el.classList.add('is-visible');

    if (typeof IntersectionObserver === 'undefined') {
      nodes.forEach(show);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          // Guard against pre-layout false positives (hero still collapsing)
          const inView =
            entry.isIntersecting &&
            entry.intersectionRatio >= 0.12 &&
            entry.boundingClientRect.top < window.innerHeight * 0.92;

          if (inView) {
            show(entry.target);
            observer.unobserve(entry.target);
          }
        }
      },
      { threshold: [0.12, 0.25, 0.5] }
    );

    let cancelled = false;
    let fallbackId = 0;

    // Wait for hero layout so below-fold sections are not marked visible on mount
    const startId = window.setTimeout(() => {
      if (cancelled) return;
      nodes.forEach((node) => {
        node.classList.remove('is-visible');
        observer.observe(node);
      });
      fallbackId = window.setTimeout(() => {
        if (!cancelled) nodes.forEach(show);
      }, 10000);
    }, 150);

    return () => {
      cancelled = true;
      window.clearTimeout(startId);
      window.clearTimeout(fallbackId);
      observer.disconnect();
    };
  }, [isStandalone]);

  const setRevealRef = (index: number) => (el: HTMLElement | null) => {
    if (el) revealRefs.current[index] = el;
  };

  const handleSignIn = () => {
    loginWithCognito();
  };

  const handleSeeHow = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    const target = document.getElementById('how-it-works');
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.history.replaceState(null, '', '#how-it-works');
  };

  const switchLocale = (next: Locale) => {
    if (next !== locale) setLocale(next);
  };

  const headline = isStandalone ? t.standaloneHeadline : t.headline;
  const supporting = isStandalone ? t.standaloneSupporting : t.supporting;

  return (
    <div className={`landing${isStandalone ? ' landing--standalone' : ''}`} lang={locale}>
      <header className="landing-hero">
        <div className="landing-hero__media" aria-hidden="true">
          <img
            src="/landing/hero.png"
            alt=""
            width={1536}
            height={1024}
            fetchPriority="high"
          />
          <div className="landing-hero__scrim" />
        </div>

        <div className="landing-hero__content">
          <nav className="landing-nav" aria-label={t.navAria}>
            <a href="#top" className="landing-brand" id="top">
              <img src="/favicon.svg" width={32} height={32} alt="" className="landing-brand__mark" draggable={false} />
              {t.brand}
            </a>
            <div className="landing-nav__actions">
              <div className="landing-lang" role="group" aria-label={t.languageAria}>
                <button
                  type="button"
                  className={`landing-lang__btn${locale === 'en' ? ' is-active' : ''}`}
                  aria-pressed={locale === 'en'}
                  onClick={() => switchLocale('en')}
                >
                  {t.langEn}
                </button>
                <button
                  type="button"
                  className={`landing-lang__btn${locale === 'es' ? ' is-active' : ''}`}
                  aria-pressed={locale === 'es'}
                  onClick={() => switchLocale('es')}
                >
                  {t.langEs}
                </button>
              </div>
              {!isStandalone && (
                <button type="button" className="landing-btn landing-btn--ghost landing-btn--nav-signin" onClick={handleSignIn}>
                  {t.signIn}
                </button>
              )}
            </div>
          </nav>

          <div className="landing-hero__copy">
            <h1>{headline}</h1>
            <p>{supporting}</p>
            <div className="landing-cta-group">
              <button
                type="button"
                className="landing-btn landing-btn--primary"
                onClick={handleSignIn}
              >
                {t.signIn}
              </button>
              {!isStandalone && (
                <a href="#how-it-works" className="landing-btn landing-btn--ghost" onClick={handleSeeHow}>
                  {t.seeHow}
                </a>
              )}
            </div>
            {!isStandalone && <p className="landing-pricing">{t.pricingNote}</p>}
            {authError && (
              <div className="landing-auth-error" role="alert">
                <strong>{t.sessionExpired}</strong> {authError.message}
              </div>
            )}
          </div>
        </div>
      </header>

      {!isStandalone && (
        <>
          <main>
            <section
              className="landing-section landing-reveal"
              ref={setRevealRef(0)}
              aria-labelledby="problem-heading"
            >
              <p className="landing-section__label">{t.problemLabel}</p>
              <h2 id="problem-heading">{t.problemTitle}</h2>
              <p className="landing-section__lead">{t.problemBody}</p>
            </section>

            <section
              id="how-it-works"
              className="landing-section landing-section--muted landing-reveal"
              ref={setRevealRef(1)}
              aria-labelledby="how-heading"
            >
              <p className="landing-section__label">{t.howLabel}</p>
              <h2 id="how-heading">{t.howTitle}</h2>
              <p className="landing-section__lead">{t.howLead}</p>
              <ol className="landing-steps">
                <li className="landing-step">
                  <span className="landing-step__num" aria-hidden="true">
                    01
                  </span>
                  <h3>{t.step1Title}</h3>
                  <p>{t.step1Body}</p>
                </li>
                <li className="landing-step">
                  <span className="landing-step__num" aria-hidden="true">
                    02
                  </span>
                  <h3>{t.step2Title}</h3>
                  <p>{t.step2Body}</p>
                </li>
                <li className="landing-step">
                  <span className="landing-step__num" aria-hidden="true">
                    03
                  </span>
                  <h3>{t.step3Title}</h3>
                  <p>{t.step3Body}</p>
                </li>
              </ol>
            </section>

            <section
              className="landing-section landing-reveal"
              ref={setRevealRef(2)}
              aria-labelledby="features-heading"
            >
              <p className="landing-section__label">{t.featuresLabel}</p>
              <h2 id="features-heading">{t.featuresTitle}</h2>
              <div className="landing-features">
                <ul className="landing-feature-list">
                  <li>
                    <h3>{t.featureControlTitle}</h3>
                    <p>{t.featureControlBody}</p>
                  </li>
                  <li>
                    <h3>{t.featureOverrunTitle}</h3>
                    <p>{t.featureOverrunBody}</p>
                  </li>
                  <li>
                    <h3>{t.featureVisualTitle}</h3>
                    <p>{t.featureVisualBody}</p>
                  </li>
                  <li>
                    <h3>{t.featurePlanningTitle}</h3>
                    <p>{t.featurePlanningBody}</p>
                  </li>
                  <li>
                    <h3>{t.featureGoalsTitle}</h3>
                    <p>{t.featureGoalsBody}</p>
                  </li>
                </ul>

                <div className="landing-abstract" aria-hidden="true">
                  <div className="landing-abstract__block landing-abstract__block--ok">
                    <header>
                      <span>{t.abstractCategoryA}</span>
                      <span>{format(t.abstractUsed, { percent: 48 })}</span>
                    </header>
                    <div className="landing-abstract__bar">
                      <i />
                    </div>
                  </div>
                  <div className="landing-abstract__block landing-abstract__block--warn">
                    <header>
                      <span>{t.abstractCategoryB}</span>
                      <span>{t.abstractNearLimit}</span>
                    </header>
                    <div className="landing-abstract__bar">
                      <i />
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section
              id="install"
              className="landing-section landing-section--muted landing-reveal"
              ref={setRevealRef(3)}
              aria-labelledby="install-heading"
            >
              <p className="landing-section__label">{t.installLabel}</p>
              <h2 id="install-heading">{t.installTitle}</h2>
              <p className="landing-section__lead">{t.installLead}</p>
              <ol className="landing-steps">
                <li className="landing-step">
                  <span className="landing-step__num" aria-hidden="true">
                    01
                  </span>
                  <h3>{t.installStep1Title}</h3>
                  <p>{t.installStep1Body}</p>
                </li>
                <li className="landing-step">
                  <span className="landing-step__num" aria-hidden="true">
                    02
                  </span>
                  <h3>{t.installStep2Title}</h3>
                  <p>{t.installStep2Body}</p>
                </li>
                <li className="landing-step">
                  <span className="landing-step__num" aria-hidden="true">
                    03
                  </span>
                  <h3>{t.installStep3Title}</h3>
                  <p>{t.installStep3Body}</p>
                </li>
              </ol>
              <p className="landing-install-android">{t.installAndroidNote}</p>
            </section>

            <section
              id="api-keys"
              className="landing-section landing-reveal"
              ref={setRevealRef(4)}
              aria-labelledby="api-keys-heading"
            >
              <p className="landing-section__label">{t.apiKeysLabel}</p>
              <h2 id="api-keys-heading">{t.apiKeysTitle}</h2>
              <p className="landing-section__lead">{t.apiKeysLead}</p>
              <div className="landing-cta-group">
                <Link to="/guide/api-keys" className="landing-btn landing-btn--primary">
                  {t.apiKeysCta}
                </Link>
              </div>
            </section>

            <section
              className="landing-section landing-section--final landing-reveal"
              ref={setRevealRef(5)}
              aria-labelledby="final-heading"
            >
              <p className="landing-section__label">{t.finalLabel}</p>
              <h2 id="final-heading">{t.finalTitle}</h2>
              <p className="landing-section__lead">{t.finalLead}</p>
              <div className="landing-cta-group">
                <button
                  type="button"
                  className="landing-btn landing-btn--primary"
                  onClick={handleSignIn}
                >
                  {t.signIn}
                </button>
              </div>
              <p className="landing-pricing landing-pricing--on-dark">{t.pricingNote}</p>
            </section>
          </main>

          <footer className="landing-footer">
            <p>{t.footer}</p>
          </footer>
        </>
      )}
    </div>
  );
}
